import React, { useState, useRef, useEffect, useCallback } from 'react';

const GeometryBuilder = () => {
  const canvasRef = useRef(null);
  const drawingRef = useRef(null);
  const [shapes, setShapes] = useState([]);
  const [selectedTool, setSelectedTool] = useState('point');
  const [question, setQuestion] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showHint, setShowHint] = useState(false);
  const [label, setLabel] = useState('A');
  const [polygonPoints, setPolygonPoints] = useState([]);
  const [triPoints, setTriPoints] = useState([]);
  const [anglePoints, setAnglePoints] = useState([]);
  const [rectStart, setRectStart] = useState(null);
  const [showHelp, setShowHelp] = useState(false);

  const tools = [
    { id: 'point', icon: '📍', label: 'Point' },
    { id: 'segment', icon: '📏', label: 'Line' },
    { id: 'circle', icon: '⭕', label: 'Circle' },
    { id: 'triangle', icon: '🔺', label: 'Triangle' },
    { id: 'rectangle', icon: '▭', label: 'Rectangle' },
    { id: 'polygon', icon: '⬡', label: 'Polygon' },
    { id: 'angle', icon: '📐', label: 'Angle' },
    { id: 'clear', icon: '🧹', label: 'Clear' }
  ];

  const fetchQuestion = useCallback(async () => {
    setLoading(true);
    setShapes([]);
    setShowHint(false);
    setLabel('A');
    setPolygonPoints([]);
    setTriPoints([]);
    setAnglePoints([]);
    setRectStart(null);
    drawingRef.current = null;
    try {
      const response = await fetch('https://eluvent.com/api/visual/geometry/question');
      const data = await response.json();
      setQuestion(data);
    } catch (error) {
      setQuestion({ title: "Draw a Circle", instruction: "Draw a circle of any radius", hint: "Use Circle tool: tap center, tap edge" });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchQuestion(); }, [fetchQuestion]);

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const W = canvas.width, H = canvas.height;
    const gridSize = 40, cx = W/2, cy = H/2;

    // Cream paper background
    ctx.fillStyle = '#fefdf8';
    ctx.fillRect(0, 0, W, H);

    // Light green grid (no numbers - just visual guide)
    ctx.strokeStyle = '#d8e8d8';
    ctx.lineWidth = 0.5;
    for (let gx = 0; gx < W; gx += gridSize) { ctx.beginPath(); ctx.moveTo(gx, 0); ctx.lineTo(gx, H); ctx.stroke(); }
    for (let gy = 0; gy < H; gy += gridSize) { ctx.beginPath(); ctx.moveTo(0, gy); ctx.lineTo(W, gy); ctx.stroke(); }

    // Red axes
    ctx.strokeStyle = '#e05050';
    ctx.lineWidth = 1.5;
    ctx.beginPath(); ctx.moveTo(cx, 0); ctx.lineTo(cx, H); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(0, cy); ctx.lineTo(W, cy); ctx.stroke();

    // Origin dot only (no numbers)
    ctx.fillStyle = '#333';
    ctx.font = 'bold 11px Arial';
    ctx.fillText('O', cx + 5, cy + 14);

    // Render all shapes
    shapes.forEach(function(shape) {
      if (shape.type === 'point') {
        ctx.fillStyle = '#e65100';
        ctx.beginPath(); ctx.arc(shape.x, shape.y, 7, 0, 2*Math.PI); ctx.fill();
        ctx.strokeStyle = '#fff'; ctx.lineWidth = 2; ctx.stroke();
        ctx.fillStyle = '#333'; ctx.font = 'bold 13px Arial';
        ctx.fillText(shape.label, shape.x + 12, shape.y - 8);
      } else if (shape.type === 'segment') {
        ctx.strokeStyle = '#1565c0'; ctx.lineWidth = 3;
        ctx.beginPath(); ctx.moveTo(shape.x1, shape.y1); ctx.lineTo(shape.x2, shape.y2); ctx.stroke();
        ctx.fillStyle = '#e65100';
        ctx.beginPath(); ctx.arc(shape.x1, shape.y1, 5, 0, 2*Math.PI); ctx.fill();
        ctx.beginPath(); ctx.arc(shape.x2, shape.y2, 5, 0, 2*Math.PI); ctx.fill();
        ctx.fillStyle = '#555'; ctx.font = 'bold 11px Arial';
        ctx.fillText(shape.label1, shape.x1 - 15, shape.y1 - 8);
        ctx.fillText(shape.label2, shape.x2 + 8, shape.y2 - 8);
        var len = Math.round(Math.hypot(shape.x2-shape.x1, shape.y2-shape.y1)/gridSize*10)/10;
        ctx.fillStyle = '#666';
        ctx.fillText(len + 'u', (shape.x1+shape.x2)/2 + 8, (shape.y1+shape.y2)/2 - 8);
      } else if (shape.type === 'circle') {
        ctx.strokeStyle = '#1565c0'; ctx.lineWidth = 3;
        ctx.beginPath(); ctx.arc(shape.x, shape.y, shape.radius, 0, 2*Math.PI); ctx.stroke();
        ctx.fillStyle = 'rgba(21,101,192,0.08)'; ctx.fill();
        ctx.fillStyle = '#e65100';
        ctx.beginPath(); ctx.arc(shape.x, shape.y, 5, 0, 2*Math.PI); ctx.fill();
        var r = Math.round(shape.radius/gridSize*10)/10;
        ctx.fillStyle = '#666'; ctx.font = 'bold 11px Arial';
        ctx.fillText('r=' + r + 'u', shape.x + shape.radius + 6, shape.y);
      } else if (shape.type === 'triangle') {
        ctx.strokeStyle = '#1565c0'; ctx.lineWidth = 3;
        ctx.beginPath();
        ctx.moveTo(shape.p1.x, shape.p1.y);
        ctx.lineTo(shape.p2.x, shape.p2.y);
        ctx.lineTo(shape.p3.x, shape.p3.y);
        ctx.closePath();
        ctx.stroke();
        ctx.fillStyle = 'rgba(21,101,192,0.08)'; ctx.fill();
        ctx.fillStyle = '#e65100';
        [shape.p1, shape.p2, shape.p3].forEach(function(pt) {
          ctx.beginPath(); ctx.arc(pt.x, pt.y, 5, 0, 2*Math.PI); ctx.fill();
        });
        ctx.fillStyle = '#333'; ctx.font = 'bold 13px Arial';
        ctx.fillText('A', shape.p1.x - 15, shape.p1.y - 8);
        ctx.fillText('B', shape.p2.x + 8, shape.p2.y - 8);
        ctx.fillText('C', shape.p3.x - 8, shape.p3.y + 18);
      } else if (shape.type === 'rectangle') {
        ctx.strokeStyle = '#1565c0'; ctx.lineWidth = 3;
        ctx.strokeRect(shape.x, shape.y, shape.w, shape.h);
        ctx.fillStyle = 'rgba(21,101,192,0.08)';
        ctx.fillRect(shape.x, shape.y, shape.w, shape.h);
        var rw = Math.round(shape.w/gridSize*10)/10;
        var rh = Math.round(shape.h/gridSize*10)/10;
        ctx.fillStyle = '#666'; ctx.font = 'bold 11px Arial';
        ctx.fillText(rw + 'u', shape.x + shape.w/2 - 10, shape.y - 8);
        ctx.fillText(rh + 'u', shape.x - 30, shape.y + shape.h/2);
      } else if (shape.type === 'polygon') {
        ctx.strokeStyle = '#1565c0'; ctx.lineWidth = 3;
        ctx.beginPath();
        ctx.moveTo(shape.points[0].x, shape.points[0].y);
        for (var pi = 1; pi < shape.points.length; pi++) ctx.lineTo(shape.points[pi].x, shape.points[pi].y);
        ctx.closePath();
        ctx.stroke();
        ctx.fillStyle = 'rgba(21,101,192,0.08)'; ctx.fill();
        ctx.fillStyle = '#e65100';
        shape.points.forEach(function(pt) {
          ctx.beginPath(); ctx.arc(pt.x, pt.y, 5, 0, 2*Math.PI); ctx.fill();
        });
      } else if (shape.type === 'angle') {
        // Draw two rays from vertex
        ctx.strokeStyle = '#1565c0'; ctx.lineWidth = 3;
        ctx.beginPath();
        ctx.moveTo(shape.vertex.x, shape.vertex.y);
        ctx.lineTo(shape.p1.x, shape.p1.y);
        ctx.stroke();
        ctx.beginPath();
        ctx.moveTo(shape.vertex.x, shape.vertex.y);
        ctx.lineTo(shape.p2.x, shape.p2.y);
        ctx.stroke();
        // Points
        ctx.fillStyle = '#e65100';
        [shape.vertex, shape.p1, shape.p2].forEach(function(pt) {
          ctx.beginPath(); ctx.arc(pt.x, pt.y, 5, 0, 2*Math.PI); ctx.fill();
        });
        // Angle arc
        var a1 = Math.atan2(shape.p1.y - shape.vertex.y, shape.p1.x - shape.vertex.x);
        var a2 = Math.atan2(shape.p2.y - shape.vertex.y, shape.p2.x - shape.vertex.x);
        ctx.strokeStyle = '#e91e63'; ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.arc(shape.vertex.x, shape.vertex.y, 30, Math.min(a1, a2), Math.max(a1, a2));
        ctx.stroke();
        // Angle value
        var angleDeg = Math.round(shape.degrees);
        ctx.fillStyle = '#e91e63'; ctx.font = 'bold 14px Arial';
        ctx.fillText(angleDeg + '°', shape.vertex.x + 35, shape.vertex.y - 10);
      }
    });

    // Show pending points (triangle, polygon, angle)
    var pendingPts = [];
    if (selectedTool === 'triangle') pendingPts = triPoints;
    else if (selectedTool === 'polygon') pendingPts = polygonPoints;
    else if (selectedTool === 'angle') pendingPts = anglePoints;

    if (pendingPts.length > 0) {
      ctx.fillStyle = '#fbbf24';
      ctx.strokeStyle = '#f59e0b';
      ctx.lineWidth = 2;
      pendingPts.forEach(function(pt) {
        ctx.beginPath(); ctx.arc(pt.x, pt.y, 6, 0, 2*Math.PI); ctx.fill();
        ctx.stroke();
      });
      // Draw lines between pending points
      if (pendingPts.length > 1) {
        ctx.strokeStyle = '#fbbf24'; ctx.lineWidth = 2; ctx.setLineDash([5, 5]);
        ctx.beginPath();
        ctx.moveTo(pendingPts[0].x, pendingPts[0].y);
        for (var k = 1; k < pendingPts.length; k++) ctx.lineTo(pendingPts[k].x, pendingPts[k].y);
        ctx.stroke();
        ctx.setLineDash([]);
      }
    }
  }, [shapes, selectedTool, triPoints, polygonPoints, anglePoints]);

  useEffect(() => { draw(); }, [draw]);

  const getCoords = (clientX, clientY) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const x = (clientX - rect.left) * scaleX;
    const y = (clientY - rect.top) * scaleY;
    const gridSize = 40;
    return { x: Math.round(x/gridSize)*gridSize, y: Math.round(y/gridSize)*gridSize };
  };

  const handleTap = (clientX, clientY) => {
    const coords = getCoords(clientX, clientY);

    if (selectedTool === 'point') {
      setShapes(function(prev) { return [...prev, { type: 'point', x: coords.x, y: coords.y, label: label }]; });
      setLabel(String.fromCharCode(label.charCodeAt(0) + 1));
    } else if (selectedTool === 'segment') {
      var d = drawingRef.current;
      if (!d) {
        drawingRef.current = { x1: coords.x, y1: coords.y };
      } else {
        setShapes(function(prev) { return [...prev, { type: 'segment', x1: d.x1, y1: d.y1, x2: coords.x, y2: coords.y, label1: label, label2: String.fromCharCode(label.charCodeAt(0)+1) }]; });
        setLabel(String.fromCharCode(label.charCodeAt(0) + 2));
        drawingRef.current = null;
      }
    } else if (selectedTool === 'circle') {
      var c = drawingRef.current;
      if (!c) {
        drawingRef.current = { cx: coords.x, cy: coords.y };
      } else {
        var r = Math.hypot(coords.x - c.cx, coords.y - c.cy);
        setShapes(function(prev) { return [...prev, { type: 'circle', x: c.cx, y: c.cy, radius: r }]; });
        drawingRef.current = null;
      }
    } else if (selectedTool === 'triangle') {
      var newTri = [...triPoints, coords];
      if (newTri.length === 3) {
        setShapes(function(prev) { return [...prev, { type: 'triangle', p1: newTri[0], p2: newTri[1], p3: newTri[2] }]; });
        setTriPoints([]);
        setLabel(String.fromCharCode(label.charCodeAt(0) + 3));
      } else {
        setTriPoints(newTri);
      }
    } else if (selectedTool === 'rectangle') {
      if (!rectStart) {
        setRectStart(coords);
      } else {
        var rw = Math.abs(coords.x - rectStart.x);
        var rh = Math.abs(coords.y - rectStart.y);
        var rx = Math.min(rectStart.x, coords.x);
        var ry = Math.min(rectStart.y, coords.y);
        setShapes(function(prev) { return [...prev, { type: 'rectangle', x: rx, y: ry, w: rw, h: rh }]; });
        setRectStart(null);
      }
    } else if (selectedTool === 'polygon') {
      setPolygonPoints([...polygonPoints, coords]);
    } else if (selectedTool === 'angle') {
      var newAng = [...anglePoints, coords];
      if (newAng.length === 3) {
        // Order: p1, vertex, p2 -> but student taps: point1, vertex, point2
        var pt1 = newAng[0];
        var vertex = newAng[1];
        var pt2 = newAng[2];
        var v1x = pt1.x - vertex.x, v1y = pt1.y - vertex.y;
        var v2x = pt2.x - vertex.x, v2y = pt2.y - vertex.y;
        var dot = v1x*v2x + v1y*v2y;
        var mag1 = Math.hypot(v1x, v1y);
        var mag2 = Math.hypot(v2x, v2y);
        var cos = mag1 > 0 && mag2 > 0 ? dot / (mag1 * mag2) : 0;
        var degrees = Math.acos(Math.max(-1, Math.min(1, cos))) * 180 / Math.PI;
        setShapes(function(prev) { return [...prev, { type: 'angle', vertex: vertex, p1: pt1, p2: pt2, degrees: degrees }]; });
        setAnglePoints([]);
      } else {
        setAnglePoints(newAng);
      }
    } else if (selectedTool === 'clear') {
      setShapes([]);
      setLabel('A');
      setPolygonPoints([]);
      setTriPoints([]);
      setAnglePoints([]);
      setRectStart(null);
      drawingRef.current = null;
    }
  };

  const finishPolygon = () => {
    if (polygonPoints.length >= 3) {
      setShapes(function(prev) { return [...prev, { type: 'polygon', points: polygonPoints }]; });
      setPolygonPoints([]);
    }
  };

  const cancelPending = () => {
    setPolygonPoints([]);
    setTriPoints([]);
    setAnglePoints([]);
    setRectStart(null);
    drawingRef.current = null;
  };

  return (
    <div style={{ minHeight: '100vh', fontFamily: 'Inter, sans-serif', background: '#f0f4f0', padding: '8px' }}>
      {/* Toolbar */}
      <div className="geo-toolbar" style={{ maxWidth: '900px', margin: '0 auto 8px', display: 'flex', gap: '6px', justifyContent: 'center', flexWrap: 'wrap' }}>
        {tools.map(function(tool) {
          return (
            <button key={tool.id} onClick={function() { setSelectedTool(tool.id); cancelPending(); }}
              style={{ padding: '10px 14px', borderRadius: 22, border: selectedTool === tool.id ? '3px solid #1565c0' : '2px solid #ddd', cursor: 'pointer', fontWeight: selectedTool === tool.id ? 700 : 500, background: selectedTool === tool.id ? '#e3f2fd' : 'white', fontSize: '13px', minHeight: 44 }}>
              {tool.icon} {tool.label}
            </button>
          );
        })}
        <button onClick={function() { setShowHelp(true); }}
          style={{ padding: '10px 14px', borderRadius: 22, border: '2px solid #fbc02d', cursor: 'pointer', fontWeight: 700, background: '#fff8e1', fontSize: '13px', color: '#8d6e00', minHeight: 44 }}>
          ❓ Help
        </button>
      </div>

      {/* Mobile responsive styles */}
      <style>{`
        @media (max-width: 768px) {
          .geo-toolbar { overflow-x: auto; flex-wrap: nowrap !important; justify-content: flex-start !important; padding-bottom: 4px; }
          .geo-toolbar button { flex-shrink: 0; font-size: 12px !important; padding: 8px 12px !important; }
          .geo-question h2 { font-size: 15px !important; }
          .geo-question p { font-size: 13px !important; }
        }
      `}</style>

      {/* Help Modal */}
      {showHelp && (
        <div
          onClick={function() { setShowHelp(false); }}
          style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div
            onClick={function(e) { e.stopPropagation(); }}
            style={{ background: 'white', borderRadius: 16, maxWidth: 500, width: '100%', maxHeight: '85vh', overflowY: 'auto', padding: 20, position: 'relative' }}>
            <button
              onClick={function() { setShowHelp(false); }}
              style={{ position: 'absolute', top: 12, right: 12, background: '#f0f0f0', border: 'none', borderRadius: '50%', width: 32, height: 32, cursor: 'pointer', fontSize: 16, fontWeight: 700 }}>✕</button>

            <h2 style={{ fontSize: 18, margin: '0 0 14px', color: '#1565c0' }}>📖 How to Use</h2>

            <div style={{ marginBottom: 14, fontSize: 14, color: '#334155', lineHeight: 1.7 }}>
              <strong style={{ color: '#1565c0' }}>🎯 General Tips:</strong>
              <ul style={{ margin: '6px 0', paddingLeft: 20 }}>
                <li>Grid is a visual guide — draw any size</li>
                <li><strong>1 unit = 1 grid square</strong></li>
                <li>Red lines are X and Y axes</li>
                <li>Shapes auto-label points (A, B, C...)</li>
              </ul>
            </div>

            <div style={{ marginBottom: 14, fontSize: 14, color: '#334155', lineHeight: 1.7 }}>
              <strong style={{ color: '#1565c0' }}>🛠️ Tool Guide:</strong>
              <ul style={{ margin: '6px 0', paddingLeft: 20 }}>
                <li><strong>📍 Point:</strong> Tap once</li>
                <li><strong>📏 Line:</strong> Tap start → tap end</li>
                <li><strong>⭕ Circle:</strong> Tap center → tap edge</li>
                <li><strong>🔺 Triangle:</strong> Tap 3 points</li>
                <li><strong>▭ Rectangle:</strong> Tap 2 corners</li>
                <li><strong>⬡ Polygon:</strong> Tap points → Finish</li>
                <li><strong>📐 Angle:</strong> Tap point → vertex → point</li>
                <li><strong>🧹 Clear:</strong> Remove all</li>
              </ul>
            </div>

            <div style={{ marginBottom: 14, fontSize: 14, color: '#334155', lineHeight: 1.7 }}>
              <strong style={{ color: '#1565c0' }}>📐 Measure Angle:</strong>
              <ol style={{ margin: '6px 0', paddingLeft: 20 }}>
                <li>Select 📐 Angle tool</li>
                <li>Tap first point</li>
                <li>Tap vertex (corner)</li>
                <li>Tap second point</li>
                <li>Angle shows in pink</li>
              </ol>
            </div>

            <div style={{ fontSize: 14, color: '#334155', lineHeight: 1.7 }}>
              <strong style={{ color: '#1565c0' }}>💡 About Units:</strong>
              <ul style={{ margin: '6px 0', paddingLeft: 20 }}>
                <li>Draw any size you want</li>
                <li>"u" = grid squares</li>
                <li>Focus on concept, not exact pixels</li>
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Pending hints */}
      {(triPoints.length > 0 || polygonPoints.length > 0 || anglePoints.length > 0 || rectStart || drawingRef.current) && (
        <div style={{ maxWidth: '900px', margin: '0 auto 8px', padding: '8px 14px', background: '#fff3cd', borderRadius: 10, textAlign: 'center', fontSize: 13, color: '#856404' }}>
          {selectedTool === 'triangle' && 'Triangle: Tap ' + (3 - triPoints.length) + ' more point(s)'}
          {selectedTool === 'polygon' && 'Polygon: Tap points, then click Finish'}
          {selectedTool === 'angle' && 'Angle: Tap ' + (3 - anglePoints.length) + ' more (point → vertex → point)'}
          {selectedTool === 'rectangle' && 'Rectangle: Tap opposite corner'}
          {selectedTool === 'segment' && 'Line: Tap endpoint'}
          {selectedTool === 'circle' && 'Circle: Tap edge'}
          {(polygonPoints.length >= 3) && (
            <button onClick={finishPolygon} style={{ marginLeft: 12, padding: '4px 14px', background: '#1565c0', color: 'white', border: 'none', borderRadius: 15, fontSize: 12, cursor: 'pointer', fontWeight: 600 }}>Finish</button>
          )}
        </div>
      )}

      {/* Question */}
      {loading ? (
        <div style={{ maxWidth: '900px', margin: '0 auto 8px', background: 'white', borderRadius: 12, padding: '40px', textAlign: 'center', borderLeft: '5px solid #1565c0' }}>
          <div style={{ fontSize: 40, animation: 'spin 2s linear infinite', display: 'inline-block' }}>⚙️</div>
          <h3 style={{ margin: '15px 0 5px', color: '#1565c0' }}>Loading Question...</h3>
          <p style={{ color: '#666', margin: 0, fontSize: 13 }}>AI is preparing a new challenge for you</p>
          <style>{'@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }'}</style>
        </div>
      ) : question && (
        <div className="geo-question" style={{ maxWidth: '900px', margin: '0 auto 8px', background: 'white', borderRadius: 12, padding: '14px', borderLeft: '5px solid #1565c0' }}>
          <h2 style={{ fontSize: 16, margin: '0 0 6px', color: '#1565c0' }}>{question.title}</h2>
          <p style={{ fontSize: 14, color: '#334155', margin: 0, lineHeight: 1.5 }}>{question.instruction}</p>
        </div>
      )}

      {/* Canvas */}
      <div style={{ maxWidth: '900px', margin: '0 auto', background: '#fefdf8', borderRadius: 12, padding: '4px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
        <canvas ref={canvasRef} width={900} height={500}
          onClick={function(e) { handleTap(e.clientX, e.clientY); }}
          onTouchStart={function(e) { e.preventDefault(); var t = e.touches[0]; handleTap(t.clientX, t.clientY); }}
          style={{ width: '100%', height: 'auto', borderRadius: 8, touchAction: 'none', display: 'block', cursor: 'crosshair' }} />
      </div>

      {/* Hint */}
      {showHint && question && question.hint && (
        <div style={{ maxWidth: '900px', margin: '8px auto 0', padding: '12px', background: '#fffde7', borderRadius: 10, fontSize: 13, color: '#8d6e00', borderLeft: '4px solid #fbc02d' }}>
          💡 <strong>Hint:</strong> {question.hint}
        </div>
      )}

      {/* Buttons */}
      <div style={{ maxWidth: '900px', margin: '12px auto 0', display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
        <button onClick={function() { setShowHint(true); }} style={{ padding: '14px 28px', background: '#fbc02d', color: 'white', border: 'none', borderRadius: 25, fontSize: 15, fontWeight: 700, cursor: 'pointer', minWidth: 130 }}>
          💡 Show Hint
        </button>
        <button onClick={fetchQuestion} style={{ padding: '14px 28px', background: '#2e7d32', color: 'white', border: 'none', borderRadius: 25, fontSize: 15, fontWeight: 700, cursor: 'pointer', minWidth: 130 }}>
          Next Question →
        </button>
      </div>

      {/* How to use - Expandable */}
      <div style={{ maxWidth: '900px', margin: '16px auto 0', background: '#e8eaf6', borderRadius: 10, overflow: 'hidden' }}>
        <button
          onClick={function() { setShowHelp(!showHelp); }}
          style={{ width: '100%', padding: '14px 16px', background: 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left', fontSize: 14, fontWeight: 700, color: '#283593', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>📖 How to Use Geometry Lab</span>
          <span style={{ fontSize: 18 }}>{showHelp ? '−' : '+'}</span>
        </button>

        {showHelp && (
          <div style={{ padding: '0 16px 16px', fontSize: 13, color: '#283593', lineHeight: 1.8 }}>

            <div style={{ marginBottom: 12 }}>
              <strong style={{ color: '#1565c0' }}>🎯 General Tips:</strong>
              <ul style={{ margin: '6px 0', paddingLeft: 20 }}>
                <li>Grid is a visual guide only — draw any size you want</li>
                <li><strong>1 unit = 1 grid square</strong> (measurements shown next to shapes)</li>
                <li>Red lines are X and Y axes, O is origin</li>
                <li>Every shape auto-labels points (A, B, C...)</li>
              </ul>
            </div>

            <div style={{ marginBottom: 12 }}>
              <strong style={{ color: '#1565c0' }}>🛠️ Tool Guide:</strong>
              <ul style={{ margin: '6px 0', paddingLeft: 20 }}>
                <li><strong>📍 Point:</strong> Tap once to place a single point</li>
                <li><strong>📏 Line:</strong> Tap start → tap end = line segment</li>
                <li><strong>⭕ Circle:</strong> Tap center → tap edge = circle with that radius</li>
                <li><strong>🔺 Triangle:</strong> Tap 3 points = triangle (auto-connects)</li>
                <li><strong>▭ Rectangle:</strong> Tap 2 opposite corners = rectangle</li>
                <li><strong>⬡ Polygon:</strong> Tap multiple points → tap Finish = closed shape</li>
                <li><strong>📐 Angle:</strong> Tap point₁ → tap vertex → tap point₂ = angle in degrees</li>
                <li><strong>🧹 Clear:</strong> Removes everything</li>
              </ul>
            </div>

            <div style={{ marginBottom: 12 }}>
              <strong style={{ color: '#1565c0' }}>📐 How to Measure an Angle:</strong>
              <ol style={{ margin: '6px 0', paddingLeft: 20 }}>
                <li>Select <strong>📐 Angle</strong> tool</li>
                <li>Tap the <strong>first point</strong> (one end of angle)</li>
                <li>Tap the <strong>vertex</strong> (corner where angle is)</li>
                <li>Tap the <strong>second point</strong> (other end of angle)</li>
                <li>Angle value appears in pink (example: 45°)</li>
              </ol>
            </div>

            <div>
              <strong style={{ color: '#1565c0' }}>💡 About Units:</strong>
              <ul style={{ margin: '6px 0', paddingLeft: 20 }}>
                <li>You don't need exact coordinates — draw any size</li>
                <li>Measurements shown as "u" (units) = grid squares</li>
                <li>For "5 units" question: count 5 grid squares</li>
                <li>What matters is the <strong>concept</strong>, not exact pixels</li>
              </ul>
            </div>

          </div>
        )}
      </div>
    </div>
  );
};

export default GeometryBuilder;
